/**
 * Links to an item from inside a cycle, track, session, pool, list, report or
 * plan say where they came from (roadmap S-37), so the item page can show that
 * cycle's rank and only that cycle's lock.
 *
 * A page that belongs to a cycle or a plan declares it once, with
 * useItemLinkScope; every link drawn under it with useItemHref carries it. A
 * link drawn with no scope is a plain link, and the item page says what it shows.
 */

import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { itemInContextHref } from './itemCycleStanding';

interface ItemLinkScope {
  cycleId?: string | null;
  planCycleId?: string | null;
}

const useScopeStore = create<{ scope: ItemLinkScope }>(() => ({ scope: {} }));

/** This page belongs to this cycle or plan, for as long as it is on screen. */
export function useItemLinkScope({ cycleId, planCycleId }: ItemLinkScope): void {
  useEffect(() => {
    if (!cycleId && !planCycleId) return;
    useScopeStore.setState({ scope: { cycleId, planCycleId } });
    return () => useScopeStore.setState({ scope: {} });
  }, [cycleId, planCycleId]);
}

/** For a link built elsewhere: an item link gets the page's cycle or plan, anything else is left alone. */
export function useScopedLink(): (href: string) => string {
  const cycleId = useScopeStore((s) => s.scope.cycleId);
  const planCycleId = useScopeStore((s) => s.scope.planCycleId);
  return useCallback(
    (href) => {
      const [path, query] = href.split('?');
      const item = /^\/prioritization\/items\/([^/]+)$/.exec(path);
      if (!item) return href;
      return itemInContextHref(item[1], { cycleId, planCycleId, tab: new URLSearchParams(query).get('tab') });
    },
    [cycleId, planCycleId],
  );
}

/** Builds a link to an item that carries the page's cycle or plan. */
export function useItemHref(): (itemId: string, tab?: string) => string {
  const cycleId = useScopeStore((s) => s.scope.cycleId);
  const planCycleId = useScopeStore((s) => s.scope.planCycleId);
  return useCallback((itemId, tab) => itemInContextHref(itemId, { cycleId, planCycleId, tab }), [cycleId, planCycleId]);
}
